<template>
  <div class="searchspring-pagination">
    <nav
      :aria-label="$string('general.pagination.label')"
      class="container searchspring-pagination__navigation"
    >
      <component
        :is="pagination.previousPage > 0 ? 'a' : 'span'"
        v-if="pages?.length > 1"
        :href="pagination.previousPage > 0 ? `${cleanUrl}${pagination.previousPage}` : null"
        class="searchspring-pagination__previous text-body-2xs-regular"
        :class="{
          [classes.disabled]: pagination.previousPage === 0
        }"
        data-button="true"
        @click.prevent="goToPrevious"
      >
        <icon-arrow-left />

        <span v-text="$string('general.pagination.previous')" />
      </component>

      <div class="searchspring-pagination__links">
        <a
          v-if="pages[0] > 1"
          :href="`${cleanUrl}1`"
          class="searchspring-pagination__link text-body-2xs-regular"
          data-button="true"
          @click.prevent="() => pageChanged(1)"
        >
          <span v-text="`1`" />
        </a>

        <span
          v-if="pages[0] > 2"
          class="searchspring-pagination__link text-body-2xs-regular"
          v-text="'...'"
        />

        <template
          v-for="page of pages"
          :key="page"
        >
          <component
            :is="page !== pagination.currentPage ? 'a' : 'span'"
            :href="page !== pagination.currentPage ? `${cleanUrl}${page}` : null"
            class="searchspring-pagination__link text-body-2xs-regular"
            :class="{
              [classes.active]: page === pagination.currentPage
            }"
            data-button="true"
            @click.prevent="() => pageChanged(page)"
          >
            <span v-text="page" />
          </component>
        </template>

        <span
          v-if="pages[pages.length - 1] < pagination.totalPages - 1"
          class="searchspring-pagination__link text-body-2xs-regular"
          v-text="'...'"
        />

        <a
          v-if="pages[pages.length - 1] < pagination.totalPages"
          :href="`${cleanUrl}${pagination.totalPages}`"
          class="searchspring-pagination__link text-body-2xs-regular"
          data-button="true"
          @click.prevent="() => pageChanged(pagination.totalPages)"
        >
          <span v-text="pagination.totalPages" />
        </a>
      </div>

      <component
        :is="pagination.nextPage > 0 ? 'a' : 'span'"
        v-if="pages?.length > 1"
        :href="pagination.nextPage > 0 ? `${cleanUrl}${pagination.nextPage}` : null"
        class="searchspring-pagination__next text-body-2xs-regular"
        :class="{
          [classes.disabled]: pagination.nextPage === 0
        }"
        data-button="true"
        @click.prevent="goToNext"
      >
        <span v-text="$string('general.pagination.next')" />

        <icon-arrow-right />
      </component>
    </nav>
  </div>
</template>

<script>

/**
 * Vue: Searchspring pagination (searchspring-pagination)
 * -----------------------------------------------------------------------------
 * A component that displays pagination options for a SearchSpring query.
 * @param {Object} pagination - The pagination data
 * @param {Boolean} isLoading - Whether the SS data is loading.
 * @param {String} url - The url to use for pagination.
 *
 */
import classes from '~/config/classes'
import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

export default {
  name: 'SearchspringPagination',

  components: {
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    pagination: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
  },

  emits: {
    'page-changed': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      classes,
      pages: [],
    }
  },

  computed: {
    cleanUrl() {
      if (!this.url.includes('?')) {
        return `${this.url}?page=`
      }

      const urlSplit = this.url.split('?')
      const cleanUrl = urlSplit[0]
      const urlParams = urlSplit[1].split('&')
      const newUrlParams = []
      urlParams.forEach((param) => {
        const split = param.split('=')
        if (split[0] === 'page') {
          return
        }

        newUrlParams.push(param)
      })

      return `${cleanUrl}?${newUrlParams.join('&')}&page=`
    },
  },

  watch: {
    pagination: {
      handler() {
        const pages = []

        // If this is the first page
        if (this.pagination.currentPage === 1) {
          pages.push(this.pagination.currentPage)

          if (this.pagination.totalPages > 1) {
            pages.push(this.pagination.nextPage)
          }

          if (this.pagination.totalPages > 2) {
            pages.push(this.pagination.nextPage + 1)
          }

          this.pages = pages
          return
        }

        // If this is the last page
        if (this.pagination.currentPage === this.pagination.totalPages) {
          if (this.pagination.totalPages > 2) {
            pages.push(this.pagination.previousPage - 1)
          }

          if (this.pagination.totalPages > 1) {
            pages.push(this.pagination.previousPage)
          }

          pages.push(this.pagination.currentPage)

          this.pages = pages
          return
        }

        // If this is a mid page
        if (this.pagination.currentPage < this.pagination.totalPages) {
          if (this.pagination.previousPage > 0) {
            pages.push(this.pagination.previousPage)
          }

          pages.push(this.pagination.currentPage)

          if (this.pagination.nextPage > 0) {
            pages.push(this.pagination.nextPage)
          }

          this.pages = pages
        }

      },
      deep: true,
      immediate: true,
    },
  },

  methods: {

    /**
     * Updates the page number in the parent component.
     * @param {Number} page - The page number
     */
    pageChanged(page) {
      if (this.isLoading || page === this.pagination.currentPage) {
        return
      }

      this.$emit('page-changed', page)
    },

    /**
     * Triggers a page update with the next page number
     */
    goToNext() {
      if (!this.pagination.nextPage) {
        return
      }

      this.pageChanged(this.pagination.nextPage)
    },

    /**
     * Triggers a page update with the previous page number
     */
    goToPrevious() {
      if (!this.pagination.previousPage) {
        return
      }

      this.pageChanged(this.pagination.previousPage)
    },
  },
}
</script>

<style lang="scss">
@import './searchspring-pagination';
</style>
